import { useEffect, useState } from 'react';
import { licenseHolderName, licenseStateOf, normalizeLicenseNumber, type BindOutcome } from '../../../supabase/functions/_shared/accountProtocol';
import { useT, type MessageKey } from '../../i18n/i18n';
import { accountStore, bindLicenseAction, refreshAccount, type BindResult } from '../../state/accountStore';
import { boundLicenseStore, cachedBoundLicense } from '../../state/boundLicense';
import { useStore } from '../../state/createStore';
import { Button, Field, InlineMessage, TextInput } from '../../ui/components';

// The inspector's New York licence (Owner decision list 2026-10-19 item 50, accountProtocol.ts L-1 … L-6),
// shared by Settings › Inspector Profile, the New Inspection licence sheet and the field-workspace gate.
//   bound    read-only: number, holder, expiry, state; changing it goes through support (L-2); an
//            expired licence says a formal report cannot be produced and can be checked again (L-6)
//   unbound  number + first name + last name as on the licence, checked against New York's public list
//            by the server (L-1); every message sits under the field it is about (Owner rule 9)
type Slot = 'number' | 'name' | 'button';

const OUTCOME_MESSAGES: Readonly<Record<Exclude<BindOutcome, 'bound'>, { slot: Slot; key: MessageKey }>> = Object.freeze({
  notListed: { slot: 'number', key: 'licence.notListed' },
  expired: { slot: 'number', key: 'licence.expiredOnList' },
  takenByAnotherAccount: { slot: 'number', key: 'licence.taken' },
  accountHasAnotherLicense: { slot: 'number', key: 'licence.accountHasAnother' },
  nameMismatch: { slot: 'name', key: 'licence.nameMismatch' },
  listUnavailable: { slot: 'button', key: 'licence.listUnavailable' }
});

const FAILURE_MESSAGES: Readonly<Record<Extract<BindResult, { kind: 'failed' }>['reason'], MessageKey>> = Object.freeze({
  offline: 'licence.offline',
  unavailable: 'licence.unavailable',
  deviceNotVerified: 'licence.deviceNotVerified'
});

export function useBoundLicense() {
  const license = useStore(boundLicenseStore, (s) => s.license);
  const loaded = useStore(boundLicenseStore, (s) => s.loaded);
  useEffect(() => {
    if (!loaded) cachedBoundLicense();
  }, [loaded]);
  return loaded ? license : cachedBoundLicense();
}

export function LicenceBinding(props: { idPrefix: string; onBound?: () => void }) {
  const { t } = useT();
  const license = useBoundLicense();
  const busy = useStore(accountStore, (s) => s.busy);
  const [licenseNumber, setLicenseNumber] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [message, setMessage] = useState<{ slot: Slot; key: MessageKey } | null>(null);

  if (license) {
    const state = licenseStateOf(license, Date.now());
    return (
      <div className="licence-bound" data-testid={`${props.idPrefix}-licence-bound`} data-state={state}>
        <div className="kv">
          <span className="kv-key">{t('licence.number')}</span>
          <span className="kv-val" data-testid={`${props.idPrefix}-licence-number`}>
            {license.licenseNumber}
          </span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('licence.holder')}</span>
          <span className="kv-val" data-testid={`${props.idPrefix}-licence-holder`}>
            {licenseHolderName(license)}
          </span>
        </div>
        <div className="kv">
          <span className="kv-key">{t('licence.expires')}</span>
          <span className="kv-val">{license.expiresOn}</span>
        </div>
        {state === 'expired' ? (
          <>
            <InlineMessage kind="err" text={t('licence.expiredNote')} />
            <Button variant="secondary" icon="refresh" disabled={busy !== null} onClick={() => void refreshAccount()} testId={`${props.idPrefix}-licence-recheck`}>
              {t('licence.recheck')}
            </Button>
          </>
        ) : null}
        <p className="card-note">{t('licence.lockedNote')}</p>
      </div>
    );
  }

  const numberOk = normalizeLicenseNumber(licenseNumber) !== null;
  const complete = numberOk && firstName.trim().length > 0 && lastName.trim().length > 0;

  async function bind() {
    setMessage(null);
    const result = await bindLicenseAction({ licenseNumber, firstName, lastName });
    if (result.kind === 'failed') {
      setMessage({ slot: 'button', key: FAILURE_MESSAGES[result.reason] });
      return;
    }
    if (result.outcome === 'bound') {
      props.onBound?.();
      return;
    }
    setMessage(OUTCOME_MESSAGES[result.outcome]);
  }

  const at = (slot: Slot) => (message && message.slot === slot ? <InlineMessage kind="err" text={t(message.key)} /> : null);

  return (
    <div className="licence-unbound form-stack" data-testid={`${props.idPrefix}-licence-unbound`}>
      <p className="card-note">{t('licence.bindBody')}</p>
      <Field label={t('licence.number')} htmlFor={`${props.idPrefix}-licence-input`}>
        <TextInput id={`${props.idPrefix}-licence-input`} value={licenseNumber} onChange={(value) => setLicenseNumber(value)} inputMode="numeric" placeholder={t('licence.numberPlaceholder')} testId={`${props.idPrefix}-licence-input`} />
      </Field>
      {at('number')}
      <Field label={t('licence.firstName')} htmlFor={`${props.idPrefix}-licence-first`}>
        <TextInput id={`${props.idPrefix}-licence-first`} value={firstName} onChange={(value) => setFirstName(value)} testId={`${props.idPrefix}-licence-first`} />
      </Field>
      <Field label={t('licence.lastName')} htmlFor={`${props.idPrefix}-licence-last`}>
        <TextInput id={`${props.idPrefix}-licence-last`} value={lastName} onChange={(value) => setLastName(value)} testId={`${props.idPrefix}-licence-last`} />
      </Field>
      {at('name')}
      <Button icon="verified_user" disabled={!complete || busy !== null} onClick={() => void bind()} testId={`${props.idPrefix}-licence-bind`}>
        {busy === 'bind' ? t('licence.binding') : t('licence.bind')}
      </Button>
      {at('button')}
    </div>
  );
}
